import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useEffect } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { LocalStorage } from "@/lib/local-storage";

// Both come from the same build (see appVersionPlugin in vite.config.ts), so they only differ once a new
// version has been deployed while this tab was still running the old bundle.
const APP_VERSION = __APP_VERSION__;
const TOAST_ID = "old-version-detector";
const DISMISSED_VERSION_KEY = "dismissed_app_version";

const appVersionSchema = z.object({ appVersion: z.string() });

export const OldVersionDetector = () => {
  const { data: freshAppVersion } = useQuery({
    queryKey: ["test-old-version"],
    queryFn: async () => {
      const resp = await axios.get("/app-version.json", { headers: { "Cache-Control": "no-cache" } });
      return appVersionSchema.parse(resp.data).appVersion;
    },
    // app-version.json is only emitted by production builds
    enabled: import.meta.env.PROD,
    refetchInterval: 1000 * 60, // check every minute
  });

  useEffect(() => {
    if (!freshAppVersion || freshAppVersion === APP_VERSION) return;
    try {
      if (LocalStorage.get(DISMISSED_VERSION_KEY, z.string()) === freshAppVersion) return;
    } catch {
      // storage unavailable — fall through and show the toast
    }

    toast.info("Newer version is available. Please refresh the page.", {
      id: TOAST_ID,
      action: { label: "Refresh", onClick: () => window.location.reload() },
      cancel: {
        label: "Dismiss",
        onClick: () => {
          try {
            LocalStorage.set(DISMISSED_VERSION_KEY, freshAppVersion);
          } catch {
            // storage unavailable — the toast will just come back next time
          }
        },
      },
      duration: Number.POSITIVE_INFINITY,
    });
  }, [freshAppVersion]);

  return null;
};
